import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Spinner,
  Text,
  Heading,
  Layer,
  TextInput,
  FormField,
  DateInput,
  MaskedInput,
} from "grommet";
import { HeaderNavigation } from "../../components/Headers/GlobalHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import API_URL from "../../config/config";
import ManagerAssessmentView from "../../components/ManagerPortal/ManagerAssessmentsView";
// import { AuthContext } from "../../contexts/AuthContext";
import { Info, LinkPrevious } from "grommet-icons";
import HeaderComponent from "../../components/ManagerPortal/HeaderComponent";
import { useSequence } from "../../components/SequenceProvider";
import moment from "moment-timezone";
import AssessmentsTourModal from "../../components/ManagerPortal/AssessmentsTourModal";

export const ManagersAssessment = () => {
  // const { user } = useContext(AuthContext);
  const { manager_id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false); // Modal visibility state
  const [assessmentName, setAssessmentName] = useState(""); // Store assessment name
  const [dueDate, setDueDate] = useState(""); // Store assessment due date
  const [dueTime, setDueTime] = useState(""); // Store assessment due time
  const [errorMessage, setErrorMessage] = useState(""); // Error message state
  const [showInfoModal, setShowInfoModal] = useState(false);

  const { startSequence } = useSequence();

  // Handle assessment creation
  const handleAssessmentCreation = async () => {
    if (!assessmentName || !dueDate || !dueTime) {
      setErrorMessage("Please provide all the required details.");
      return;
    }
    
    // Combine date and time into a single DATETIME string
    const dateTime = `${dueDate} ${dueTime}:00`; // Combine with seconds
    // Check if this date-time is in the past
    const selectedDateTime = new Date(dateTime);
    const now = new Date();
    if (selectedDateTime < now) {
      setErrorMessage("Due date/time cannot be in the past.");
      return;
    }
    const usertimezone = moment.tz.guess();
    setLoading(true);
    // Get the current date, time, and time zone
    const currentDateTime = moment().format("YYYY-MM-DD HH:mm:ss");
    const currentTimeZone = moment.tz.guess();

    
    
    try {
      const res = await axios.post(
        `${API_URL}/manager/create_assessment/${manager_id}`,
        {
          name: assessmentName,
          dueBy: dateTime, // Pass the combined date and time
          timezone: usertimezone,
          created_at: currentDateTime,
          creation_tz: currentTimeZone,
        },
      );
      if (res.status === 201 && res.data) {
        const assessment_id = res.data.assessment_id;
        startSequence();
        navigate(`/manager/${manager_id}/${assessment_id}/select-users`);
      } else {
        console.error("An unknown error occurred, please try again!");
      }
    } catch (error) {
      
    } finally {
      setLoading(false);
      setShowModal(false); // Close the modal after creating the assessment
    }
  };
  // const handleRetake = () => {
  //   
  //   navigate(`/add-proficiency/${user.user_table_id}`);
  // };

  const handleAssessmentArchive = (manager_id) => {
    navigate(`/manager/assessment-archive/${manager_id}`);
  };

  if (loading) {
    return (
      <Box fill align="center" justify="center">
        <Spinner size="large" />
        <Text>Creating a new assessment for you...</Text>
      </Box>
    );
  }

  return (
    <>
      <Box fill="vertical" justify="between">
        <HeaderNavigation />
        <HeaderComponent
          current={"Manage Assessments"}
          previous={"Dashboard"}
          current_link_name={"assessments"}
          previous_link_name={"dashboard"}
          color_modify={2}
          required={1}
        />
        <Box flex="grow">
          {/* Global Header */}
          <Box
            pad="medium"
            background="light-2"
            round="small"
            elevation="small"
            margin={{ bottom: "medium", top: "small", horizontal: "small" }}
          >
            <Box direction="row" align="center" gap="small">
              <Info size="medium" color="brand" />
              <Heading level={3} margin="none" color="brand">
                Manage assessments
              </Heading>
            </Box>
            <Text margin={{ top: "small" }} size="medium">
              This section allows you to create new assessments, access archived
              assessments (assessments with past due dates), and view all active
              assessments along with key details such as title, due date,
              selected skills, and participant count. Once an assessment’s due
              date has passed, it is automatically moved to the archived
              section. You can also view detailed assessment information, update
              the assessment’s date and time, and manage users associated with
              each assessment. To learn more about this page,{" "}
              <Text
                as="span"
                style={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => setShowInfoModal(true)}
              >
                click here.
              </Text>
            </Text>
          </Box>

          {/* Main Content Section */}
          <Box flex direction="column" gap="medium">
            {/* Top Right Button */}
            <Box
              direction="row"
              justify="end"
              margin={{ horizontal: "medium", top: "small" }}
              gap="small"
              flex={false}
            >
              <Button
                label="Create Assessment"
                title="Create a new assessment"
                onClick={() => setShowModal(true)}
                secondary
                style={{ flexShrink: 0 }}
              />
              <Button
                label="Archived Assessments"
                title="View legacy assessments"
                onClick={() => handleAssessmentArchive(manager_id)}
                secondary
                style={{ flexShrink: 0 }}
              />
            </Box>

            <ManagerAssessmentView manager_id={manager_id} />
          </Box>
          {/* Modal for Assessment Name, Due Date, and Time */}
          {showModal && (
            <Layer
              onEsc={() => setShowModal(false)}
              onClickOutside={() => setShowModal(false)}
            >
              <Box pad="medium" gap="small" width="medium">
                <Heading level={3} margin="none">
                  Create new assessment
                </Heading>
                {errorMessage && (
                  <Text color="status-critical" margin={{ bottom: "small" }}>
                    {errorMessage}
                  </Text>
                )}
                <FormField label="Assessment name" required>
                  <TextInput
                    value={assessmentName}
                    onChange={(event) => setAssessmentName(event.target.value)}
                    placeholder="Enter assessment name"
                  />
                </FormField>
                <FormField label="Due by (Date)" required>
                  <DateInput
                    format="yyyy-mm-dd"
                    value={dueDate}
                    onChange={({ value }) => {
                      // Ensure the date is properly formatted as YYYY-MM-DD
                      const formattedDate = Array.isArray(value)
                        ? new Date(value[0]).toLocaleDateString("en-CA") // Single date from array
                        : new Date(value).toLocaleDateString("en-CA"); // Handle direct value

                      setDueDate(formattedDate);
                    }}
                    calendarProps={{
                      range: false, // Single date selection only
                    }}
                    min={new Date().toLocaleDateString("en-CA")} // Prevent manual entry of past dates
                  />
                </FormField>
                <FormField label="Due by (Time)" required>
                  <MaskedInput
                    mask={[
                      {
                        length: [1, 2],
                        options: Array.from({ length: 24 }, (v, k) =>
                          k.toString().padStart(2, "0"),
                        ),
                        placeholder: "hh",
                      },
                      { fixed: ":" },
                      {
                        length: 2,
                        options: ["00", "15", "30", "45"],
                        placeholder: "mm",
                      },
                    ]}
                    value={dueTime}
                    onChange={(event) => setDueTime(event.target.value)}
                    placeholder="Enter time (HH:MM)"
                  />
                </FormField>
                <Box
                  direction="row"
                  gap="small"
                  justify="between"
                  margin={{ top: "medium" }}
                >
                  <Button label="Cancel" onClick={() => setShowModal(false)} />
                  <Button
                    label="Create assessment"
                    primary
                    onClick={handleAssessmentCreation} // Explicitly call function on button click
                  />
                </Box>
              </Box>
            </Layer>
          )}
          <AssessmentsTourModal
            showModal={showInfoModal}
            setShowModal={setShowInfoModal}
          />
          {/* Global Footer */}
          <StickyFooter />
        </Box>
      </Box>
    </>
  );
};
